const THREATS = [
  {
    threat: "Vault database theft",
    surface: "SQLite file on disk",
    mitigation: "AES-256-GCM per-entry encryption — stolen database is useless without the vault key",
    severity: "High", sevColor: "text-red-300", sevBg: "bg-red-500/10 border-red-500/30",
  },
  {
    threat: "Master password brute-force",
    surface: "Password input or offline attack",
    mitigation: "Argon2id with 64 MB memory cost and 3 passes — makes brute-force computationally expensive and GPU-resistant",
    severity: "High", sevColor: "text-red-300", sevBg: "bg-red-500/10 border-red-500/30",
  },
  {
    threat: "Memory scraping",
    surface: "Process memory after lock",
    mitigation: "Vault key stored in a mutable bytearray, zeroed immediately on lock — key does not linger in memory",
    severity: "Medium", sevColor: "text-amber-300", sevBg: "bg-amber-500/10 border-amber-500/30",
  },
  {
    threat: "Clipboard interception",
    surface: "Clipboard after password copy",
    mitigation: "Clipboard auto-cleared after 30 seconds — limits exposure window",
    severity: "Medium", sevColor: "text-amber-300", sevBg: "bg-amber-500/10 border-amber-500/30",
  },
  {
    threat: "Credential exfiltration via extension",
    surface: "Native messaging channel",
    mitigation: "allowed_origins and allowed_extensions whitelisted in native host manifest — only the registered extension ID can communicate with the host",
    severity: "Medium", sevColor: "text-amber-300", sevBg: "bg-amber-500/10 border-amber-500/30",
  },
  {
    threat: "Phishing autofill",
    surface: "Extension on a spoofed login page",
    mitigation: "Credentials are matched by domain — only entries for the exact current domain are returned to the extension",
    severity: "Medium", sevColor: "text-amber-300", sevBg: "bg-amber-500/10 border-amber-500/30",
  },
  {
    threat: "Vault key on disk",
    surface: "Wrapped key file",
    mitigation: "Vault key is always wrapped (AES-256-GCM from Argon2id, plus optional DPAPI) — plaintext key is never written to disk",
    severity: "High", sevColor: "text-red-300", sevBg: "bg-red-500/10 border-red-500/30",
  },
  {
    threat: "Cloud sync / remote exfiltration",
    surface: "Network",
    mitigation: "Zero cloud dependency by design — no network calls during any credential operation. All data stays on-device.",
    severity: "Low", sevColor: "text-emerald-300", sevBg: "bg-emerald-500/10 border-emerald-500/30",
  },
];

const PROPERTIES = [
  { label: "Confidentiality", val: "AES-256-GCM per entry — ciphertext reveals nothing without the vault key" },
  { label: "Integrity",       val: "GCM 128-bit auth tag on every entry — tampered ciphertext fails to decrypt" },
  { label: "Availability",    val: "Fully offline — no internet required. DPAPI enables silent unlock for speed." },
  { label: "Non-repudiation", val: "Native host log retains all operations with timestamps, passwords redacted" },
];

export default function SecurityModel() {
  return (
    <div className="space-y-4">
      <div>
        <p className="font-mono text-[10px] tracking-[0.25em] text-slate-500">SECURITY MODEL</p>
        <h4 className="mt-1 text-sm font-semibold text-white">Threat model and attack surface</h4>
        <p className="mt-1 text-xs text-slate-400">
          Every design decision maps back to a specific threat. The goal is a vault that is useless to an attacker even if they have full read access to the user's disk.
        </p>
      </div>

      {/* Threat table */}
      <div className="overflow-x-auto rounded-xl border border-white/10 bg-black/20">
        <table className="w-full min-w-[540px] text-left">
          <thead>
            <tr className="border-b border-white/10">
              <th className="py-2.5 pl-3 pr-2 font-mono text-[9px] tracking-[0.15em] text-slate-500">THREAT</th>
              <th className="px-2 py-2.5 font-mono text-[9px] tracking-[0.15em] text-slate-500">SURFACE</th>
              <th className="px-2 py-2.5 font-mono text-[9px] tracking-[0.15em] text-slate-500">MITIGATION</th>
              <th className="px-2 py-2.5 font-mono text-[9px] tracking-[0.15em] text-slate-500">SEV</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {THREATS.map((t) => (
              <tr key={t.threat} className="hover:bg-white/[0.02]">
                <td className="py-2.5 pl-3 pr-2 text-xs font-medium text-slate-200">{t.threat}</td>
                <td className="px-2 py-2.5 text-[11px] text-slate-400">{t.surface}</td>
                <td className="px-2 py-2.5 text-[11px] text-slate-400">{t.mitigation}</td>
                <td className="px-2 py-2.5">
                  <span className={`rounded border px-1.5 py-0.5 font-mono text-[9px] font-bold ${t.sevBg} ${t.sevColor}`}>{t.severity}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Security properties */}
      <div className="rounded-xl border border-white/10 bg-black/20 p-3">
        <p className="mb-2 font-mono text-[9px] tracking-[0.15em] text-slate-500">SECURITY PROPERTIES</p>
        <div className="grid gap-2 sm:grid-cols-2">
          {PROPERTIES.map(({ label, val }) => (
            <div key={label} className="rounded-lg border border-white/10 bg-white/5 p-2.5">
              <p className="text-[10px] font-bold text-violet-300">{label}</p>
              <p className="mt-0.5 text-[11px] leading-relaxed text-slate-400">{val}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Zero cloud callout */}
      <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-3">
        <p className="font-mono text-[10px] tracking-[0.2em] text-emerald-400">DESIGN PRINCIPLE — LOCAL FIRST</p>
        <p className="mt-1 text-xs text-slate-300">
          SecureVault makes zero network connections during any credential operation. There is no sync server, no telemetry endpoint, and no cloud backup path. The entire attack surface is local — which means an attacker needs physical or remote access to the machine, and even then the encrypted vault is computationally infeasible to crack without the vault key.
        </p>
      </div>
    </div>
  );
}
